const API_URL: string = process.env.REACT_APP_API_URL || 'http://localhost:8000'
const AUTH_RESULT_PARAMS = ['auth', 'authResult']
const BANK_AUTH_SESSION_KEY = 'family-bank-auth-checked'
const BANK_AUTH_NAME_KEY = 'family-bank-auth-name'
const FAILED_AUTH_RESULTS = new Set(['missing-code', 'expired', 'denied', 'failed', 'missing-name', 'unknown-name'])
let authorizationRedirectStarted = false
let authorizationCheckPromise: Promise<void> | undefined

const isLocalDevelopmentApi = (): boolean => {
	if (process.env.NODE_ENV !== 'development') return false
	try {
		const apiUrl = new URL(API_URL)
		return apiUrl.hostname === 'localhost' || apiUrl.hostname === '127.0.0.1' || apiUrl.hostname === '::1'
	} catch {
		return false
	}
}

const hasFailedAuthorizationResult = (): boolean => {
	return bankAuthorizationResult() !== undefined
}

export const bankAuthorizationResult = (): string | undefined => {
	const params = new URLSearchParams(window.location.search)
	for (const param of AUTH_RESULT_PARAMS) {
		const authResult = params.get(param)
		if (authResult !== null && FAILED_AUTH_RESULTS.has(authResult)) {
			return authResult
		}
	}
	return undefined
}

export const consumeSuccessfulBankAuthorization = (): boolean => {
	const url = new URL(window.location.href)
	if (url.searchParams.get('auth') !== 'success' && url.searchParams.get('authResult') !== 'success') {
		return false
	}
	markAuthorizationChecked()
	url.searchParams.delete('auth')
	url.searchParams.delete('authResult')
	window.history.replaceState({}, '', url.toString())
	return true
}

const bankReturnToUrl = (): string => {
	const url = new URL(window.location.href)
	url.searchParams.delete('auth')
	url.searchParams.delete('authResult')
	return url.toString()
}

const mobileAuthorizationReturnToUrl = (name: string): string => {
	const params = new URLSearchParams({ name })
	return `${window.location.origin}/banca/mobile-auth?${params.toString()}`
}

export const requestBankMobileAuthorizationUrl = async (name: string): Promise<string> => {
	const params = new URLSearchParams({
		name,
		returnTo: mobileAuthorizationReturnToUrl(name),
	})
	const response = await fetch(`${API_URL}/auth/mobile-url?${params.toString()}`, {
		credentials: 'include',
	})
	if (!response.ok) {
		throw new Error('Unable to create mobile authorization URL')
	}
	const payload = await response.json() as { authorizeUrl?: string }
	if (!payload.authorizeUrl) {
		throw new Error('Mobile authorization URL missing')
	}
	return payload.authorizeUrl
}

export const pollBankAuthorizationStatus = async (): Promise<boolean> => {
	const response = await fetch(`${API_URL}/auth/status`, {
		credentials: 'include',
	})
	if (response.status >= 200 && response.status < 300) {
		markAuthorizationChecked()
		return true
	}
	return false
}

const startAuthorization = (): boolean => {
	if (hasFailedAuthorizationResult()) return false
	if (authorizationRedirectStarted) return false
	const name = bankAuthorizationName()
	if (!name) return false
	authorizationRedirectStarted = true
	const returnTo = encodeURIComponent(bankReturnToUrl())
	window.location.href = `${API_URL}/auth?returnTo=${returnTo}&name=${encodeURIComponent(name)}`
	return true
}

const clearStoredAuthorization = () => {
	sessionStorage.removeItem(BANK_AUTH_SESSION_KEY)
}

const markAuthorizationChecked = () => {
	sessionStorage.setItem(BANK_AUTH_SESSION_KEY, 'true')
}

const hasStoredAuthorizationCheck = (): boolean => {
	return sessionStorage.getItem(BANK_AUTH_SESSION_KEY) === 'true'
}

export const bankAuthorizationName = (): string => {
	return sessionStorage.getItem(BANK_AUTH_NAME_KEY)?.trim() || ''
}

export const saveBankAuthorizationName = (name: string) => {
	sessionStorage.setItem(BANK_AUTH_NAME_KEY, name.trim())
}

export const hasBankAuthorizationSession = (): boolean => {
	return isLocalDevelopmentApi() || hasStoredAuthorizationCheck()
}

const requiresBankAuthorization = (resource: string): boolean => {
	return resource === '/bank' || resource.startsWith('/bank/') || resource.startsWith('/bank?')
}

const checkAuthorization = async () => {
	if (hasFailedAuthorizationResult()) {
		throw new Error('Authorization failed')
	}
	if (isLocalDevelopmentApi()) return
	if (hasStoredAuthorizationCheck()) return
	const response = await fetch(`${API_URL}/auth`, {
		credentials: 'include',
		headers: {
			'X-Requested-With': 'fetch',
		},
	})
	if (response.status >= 200 && response.status < 300) {
		markAuthorizationChecked()
		return
	}
	if (response.status === 401 && startAuthorization()) {
		throw new Error('Authorization required')
	}
	throw new Error('Authorization failed')
}

const ensureAuthorization = async () => {
	if (authorizationCheckPromise) return authorizationCheckPromise
	authorizationCheckPromise = checkAuthorization().finally(() => {
		authorizationCheckPromise = undefined
	})
	return authorizationCheckPromise
}

export const apiFetch = async (resource: string, init: RequestInit = {}): Promise<Response> => {
	if (requiresBankAuthorization(resource)) {
		await ensureAuthorization()
	}
	const response = await fetch(`${API_URL}${resource}`, {
		...init,
		credentials: 'include',
	})
	if (response.status === 401 && requiresBankAuthorization(resource)) {
		clearStoredAuthorization()
		if (!startAuthorization()) {
			throw new Error('Authorization failed')
		}
		throw new Error('Authorization required')
	}
	if (response.redirected && response.url.includes('/authorize')) {
		window.location.href = response.url
		throw new Error('Authorization required')
	}
	return response
}

export const recoverApiAuthorization = (error: unknown) => {
	if (error instanceof TypeError) {
		startAuthorization()
	}
}
